import React, { useEffect, useRef, memo } from "react";
import { Box, Typography, Button, Collapse, IconButton } from "@mui/material";
import DirectionsIcon from "@mui/icons-material/Directions";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlaceIcon from "@mui/icons-material/Place";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { COLORS } from "./constants";

const MobileStationPanel = memo(({ stations, selectedStation, setSelectedStation, searchedLocation, isLoading, allStations }) => {
  const navigate = useNavigate();
  const lastClickedCardId = useRef(null);

  // Auto-scroll to selected station
  useEffect(() => {
    if (selectedStation) {
      if (lastClickedCardId.current === selectedStation.id) {
         lastClickedCardId.current = null;
         return;
      }
      const element = document.getElementById(`mobile-card-${selectedStation.id}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [selectedStation]);

  const handleDirections = (e, s) => {
    e.stopPropagation();
    const url = s.lat && s.lon 
      ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lon}` 
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.location)}`;
    window.open(url, "_blank");
  };

  const handleViewDetails = (e, s) => {
    e.stopPropagation();
    navigate("/stations/station_details", { state: { currentStation: s, station: s, allStations } });
  };

  return (
    <Box 
      sx={{ 
        width: "100%",
        height: "100%", 
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8f9fa",
      }}
    >
      <Box 
        sx={{ 
          py: 1.5, px: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          bgcolor: "#fff", 
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          flexShrink: 0
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 800, color: "#333" }}>
           {stations.length > 0 ? `${stations.length} Nearby` : "Nearby Stations"}
        </Typography>

        {selectedStation && (
           <IconButton 
              size="small" 
              onClick={() => setSelectedStation(null)} 
              sx={{ bgcolor: "#f5f5f5", ml: 1 }}
           >
              <CloseIcon fontSize="small" />
           </IconButton>
        )}
      </Box>

      {/* --- SCROLLABLE LIST --- */}
      <Box 
        sx={{ 
          flex: 1, 
          overflowY: "auto", 
          p: 1.5, 
          pb: 12, 
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch"
        }}
      >
        {isLoading ? (
             <Box textAlign="center" py={6} color="#888">Finding stations...</Box>
        ) : stations.length > 0 ? (
          stations.map((s) => {
            const isSelected = selectedStation?.id === s.id;
            
            return (
            <Box 
              id={`mobile-card-${s.id}`}
              key={s.id} 
              onClick={() => {
                if (!isSelected) {
                    lastClickedCardId.current = s.id;
                }
                setSelectedStation(isSelected ? null : s);
              }} 
              sx={{ 
                mb: 1.5, 
                bgcolor: "#fff", 
                borderRadius: "16px", 
                overflow: "hidden",
                border: isSelected ? `2px solid ${COLORS.primary}` : "1px solid transparent",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)", 
                transition: "all 0.2s ease"
              }}
            >
              <Box sx={{ display: "flex", p: 1.5 }}> 
                
                {/* SMALLER IMAGE (65px vs 85px) */}
                <Box sx={{ width: 65, height: 65, borderRadius: "12px", overflow: "hidden", bgcolor: "#eee", flexShrink: 0 }}>
                  <img 
                    src={s.thumbnail || s.mainImage || "/default-station.png"} 
                    alt="" 
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </Box>

                {/* TEXT CONTENT */}
                <Box sx={{ ml: 1.5, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  
                  {/* Station Name */}
                  <Typography noWrap sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#2d3436", mb: 0.2, lineHeight: 1.2 }}>
                    {s.location}
                  </Typography>
                  
                  {/* Province */}
                  <Typography noWrap sx={{ fontSize: "0.75rem", color: COLORS.textSecondary, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
                    {s.province}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                     <Box sx={{ bgcolor: COLORS.primaryLight, px: 1, py: 0.2, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PlaceIcon sx={{ fontSize: 12, color: COLORS.primary }} />
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: COLORS.primaryDark }}>
                            {s.distance?.toFixed(1)} km
                        </Typography>
                     </Box>
                  </Box>
                </Box>
              </Box>

              {/* ACTION BUTTONS */}
              <Collapse in={isSelected} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                    <Box sx={{ display: "flex", gap: 1, pt: 1, borderTop: "1px dashed #eee" }}>
                      <Button 
                          fullWidth 
                          variant="outlined"
                          size="small"
                          onClick={(e) => handleDirections(e, s)}
                          startIcon={<DirectionsIcon fontSize="small" />}
                          sx={{ 
                            borderRadius: "10px", 
                            color: "#555", 
                            borderColor: "#ddd",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            textTransform: "none",
                            py: 0.8 
                          }}
                      >
                          Directions
                      </Button>
                      
                      <Button 
                          fullWidth 
                          variant="contained"
                          size="small"
                          onClick={(e) => handleViewDetails(e, s)}
                          startIcon={<ArrowForwardIcon fontSize="small" />}
                          sx={{ 
                            borderRadius: "10px", 
                            bgcolor: COLORS.primary,
                            color: "#fff",
                            fontWeight: 700, 
                            fontSize: "0.8rem", 
                            textTransform: "none",
                            py: 0.8, 
                            boxShadow: "none"
                          }}
                      >
                          Details
                      </Button>
                    </Box>
                  </Box>
              </Collapse>
            </Box>
          )})
        ) : (
          <Box textAlign="center" py={8} px={4}>
             <Typography sx={{ color: "#999", fontSize: "0.9rem" }}>
                {searchedLocation ? "No stations found." : "Search to find stations"}
             </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default MobileStationPanel;